// ── AssistantContext — Evo Brain, montado UNA vez arriba de <Routes> ─────────
// Antes: cada *ClaudePanel llamaba useAssistant(entityType, entityId) con
// estado 100% local — al desmontar la página (cambio de ruta), se perdía la
// conversación visible y cualquier stream SSE en curso quedaba huérfano
// (nadie escuchaba el resto). Ahora: el estado real vive acá, en un Provider
// montado una sola vez en AppShell — cambiar de ruta ya no lo toca.
//
// Estado por-entidad (mensajes, streaming, pills, undo, approve/reject) vive
// en un Record<entityKey, EntityState> — cambiar de entidad activa NO cancela
// ni resetea la de antes: si dejaste un lead generando y navegás a un
// proyecto, esa generación sigue sola en background y se persiste igual.
// TTS (muted/isSpeaking/broken) es GLOBAL a propósito — hay un solo canal de
// audio real (defaultTtsProvider), no tiene sentido que sea "por entidad".
//
// Las páginas no renderizan el panel visual — eso lo hace AssistantDock
// (montado una vez en AppShell). Las páginas solo "se registran" vía
// useAssistantEntity(entityType, entityId, section, config): siguen dueñas
// de SU lógica propia (chips, quick actions, link de la entidad para la
// ApprovalCard) — eso no se pierde en la consolidación, solo se saca la UI
// genérica duplicada (bubbles, tarjetas, indicadores) que vivía repetida en
// los 9 paneles.

import {
  createContext, useContext, useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { defaultTtsProvider, extractSpeakableText } from '../lib/tts.js';
import type {
  AssistantMessage, AssistantPill, ProposalVerb, ProposalSummary, StreamingState,
} from '../hooks/useAssistant.js';

const TTS_MUTED_KEY = 'evo-assistant-muted';
const TTS_BROKEN_KEY = 'evo-assistant-tts-broken';
const TTS_BROKEN_MSG_KEY = 'evo-assistant-tts-broken-msg';

// ── Tipos ─────────────────────────────────────────────────────────────────────

export interface AssistantModuleConfig {
  title: string;
  placeholder: string;
  inputDisabled?: boolean;
  emptyHint: string;
  topContent?: ReactNode;
  /** Herramientas del módulo, en su propia pestaña del dock (ver AssistantShell). */
  toolsContent?: ReactNode;
  /** Pestaña abierta. Sin esto el dock la maneja solo. */
  activeTab?: 'chat' | 'tools';
  onActiveTabChange?: (tab: 'chat' | 'tools') => void;
  entityLink?: { label: string; href: string } | null;
  chipPlaceholder?: string;
  /** Override del render del texto de la respuesta — hoy solo email lo usa (SafeLink, bloqueo de links de phishing). Default: Markdown plano. */
  renderAssistantContent?: (content: string) => ReactNode;
  /** Draft en curso (compose de email) — el input genérico del dock lo reenvía en cada mensaje, ver AssistantDock. */
  composeContext?: ComposeContext;
}

interface EntityState {
  messages: AssistantMessage[];
  /** Hay conversación de días anteriores que el panel no está mostrando
   *  (2026-08-31: el panel arranca limpio cada día). */
  hasOlderMessages: boolean;
  /** El usuario pidió ver todo y ya se cargó — no volver a ofrecerlo. */
  showingFullHistory: boolean;
  /** La conversación ya se pidió al server (haya o no mensajes). Sin esto,
   *  "vacía" y "todavía cargando" son indistinguibles — y el panel de inducción
   *  saludaba de nuevo antes de enterarse de que ya había saludado. */
  loaded: boolean;
  conversationId: number | null;
  pills: AssistantPill[];
  streaming: StreamingState;
  error: string | null;
  voiceNote: string | null;
  isUndoing: boolean;
  approvingTaskId: string | null;
  rejectingTaskId: string | null;
  bulkApproving: boolean;
  bulkRejecting: boolean;
}

const EMPTY_ENTITY_STATE: EntityState = {
  messages: [], hasOlderMessages: false, showingFullHistory: false, loaded: false,
  conversationId: null, pills: [], streaming: { phase: 'idle' }, error: null,
  voiceNote: null, isUndoing: false, approvingTaskId: null, rejectingTaskId: null,
  bulkApproving: false, bulkRejecting: false,
};

type SseEvent =
  | { type: 'thinking' }
  | { type: 'tool_executing'; toolName: string; label: string }
  | { type: 'tool_result'; toolName: string; humanSummary: string; messageId: number; undoable: boolean; result?: unknown }
  | { type: 'proposal'; messageId: number; taskId: string; turnGroupId: string; toolName: string; verb: ProposalVerb; label: string; summary: ProposalSummary }
  | { type: 'text'; content: string; voice?: string | null }
  | { type: 'done'; conversationId: number }
  | { type: 'error'; error: string };

const INVALIDATION_KEYS: Record<string, string[][]> = {
  project: [['projects'], ['project']],
  lead: [['leads']],
  contact: [['contacts'], ['contact']],
  report: [['reports']],
  email_message: [['email-messages'], ['email-thread'], ['email-detail'], ['email-categories'], ['email-drafts']],
  task: [['tasks'], ['task']],
};

// ── Un solo hilo para el panel (2026-08-31) ─────────────────────────────────
// El estado del asistente se guardaba por entidad: una conversación por
// proyecto, por lead, por contacto. Eduardo pidió lo contrario — un chat
// lineal, "así puedo ver una secuencia más lógica de las cosas que le voy
// pidiendo" — así que todas las pantallas comparten el mismo hilo y el mismo
// estado.
//
// Ojo con lo que NO cambia: el entityType/entityId del panel se sigue mandando
// en CADA mensaje (ver sendMessage). El hilo es uno, el contexto de la acción
// sigue siendo el de la pantalla donde estás: eso es lo que hace que "cambiá el
// verde por azul" sepa de qué página habla, y lo que el servidor guarda como
// etiqueta en cada mensaje para poder leer después la bitácora de un cliente.
//
// 'dm_thread' queda aparte porque no es una conversación con Eduardo: es el
// diálogo automático con un cliente final (ver AGENT_CONVERSATION_TYPES en el
// servidor). Su estado no puede compartir hilo con el panel.
const AGENT_ENTITY_TYPES = new Set(['dm_thread']);
const USER_THREAD_KEY = 'global::';

function entityKeyOf(entityType: string, entityId: string | null): string {
  if (AGENT_ENTITY_TYPES.has(entityType)) return `${entityType}::${entityId ?? ''}`;
  return USER_THREAD_KEY;
}

// open_module/open_entity (tools/global.ts) devuelven { navigate: { path } }
// en result — result es unknown (viene de cualquier tool), así que se valida
// forma antes de confiar en el string.
function extractNavigatePath(result: unknown): string | null {
  if (result == null || typeof result !== 'object') return null;
  const navigate = (result as Record<string, unknown>)['navigate'];
  if (navigate == null || typeof navigate !== 'object') return null;
  const path = (navigate as Record<string, unknown>)['path'];
  return typeof path === 'string' && path.length > 0 ? path : null;
}

// ── API expuesta por entidad (misma forma que el viejo useAssistant()) ──────

// Estado del form de compose en el browser (nunca persistido) — se manda
// junto al mensaje para que el dock sepa qué email se está redactando
// AHORA MISMO, sin depender de que ya exista un draft guardado en DB.
export interface ComposeContext {
  recipientEmail?: string;
  contactName?: string;
  subject?: string;
  body?: string;
}

export interface EntityAssistantApi extends EntityState {
  muted: boolean;
  toggleMuted: () => void;
  isSpeaking: boolean;
  stopSpeaking: () => void;
  ttsBroken: boolean;
  ttsErrorMessage: string | null;
  reactivateTts: () => void;
  sendMessage: (message: string, composeContext?: ComposeContext) => Promise<void>;
  undoMessage: (messageId: number) => Promise<void>;
  approveProposal: (taskId: string, remember?: boolean) => Promise<void>;
  rejectProposal: (taskId: string) => Promise<void>;
  approveAllProposals: (taskIds: string[]) => Promise<void>;
  rejectAllProposals: (taskIds: string[]) => Promise<void>;
  /** Carga la conversación completa (días anteriores incluidos) en el panel. */
  loadFullHistory: () => void;
}

interface ActiveEntry {
  entityType: string;
  entityId: string | null;
  config: AssistantModuleConfig;
}

// ── ActiveStore — el porqué de este archivo (2026-08-05, incidente real) ────
// Bug original: useRegisterAssistantActive escribía "quién está activo" como
// React state DENTRO del mismo Context que las páginas consumen para leer su
// propia conversación (useAssistantModule → useContext(AssistantCtx)). Cada
// escritura cambiaba el `value` del Provider → TODO consumidor de ese
// Context se re-renderizaba, incluida la página que acababa de escribir →
// su propio efecto de registro volvía a correr → volvía a escribir → loop
// infinito → "Maximum update depth exceeded" sin ErrorBoundary → pantalla
// en negro en producción.
//
// Fix real (no alcanza con un array de dependencias más prolijo — el propio
// getApi() devuelve un objeto/funciones nuevas en cada llamada, así que
// hasta un dependency array "correcto" seguiría dependiendo de referencias
// inestables). Se separa "quién está activo" a un store chico, fuera de
// React state, con su propio mecanismo de suscripción (useSyncExternalStore,
// el hook que React 18 da exactamente para esto). Quien ESCRIBE (los
// paneles, vía useRegisterAssistantActive) nunca se suscribe a este store —
// solo AssistantDock lo hace. Escribir ya no puede volver a re-renderizar a
// quien escribió: estructuralmente no hay vuelta atrás posible, no importa
// si el efecto de registro corre en cada render o no.
class ActiveStore {
  private entry: ActiveEntry | null = null;
  private listeners = new Set<() => void>();

  getSnapshot = (): ActiveEntry | null => this.entry;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  set(entry: ActiveEntry): void {
    this.entry = entry;
    this.notify();
  }

  /** Actualiza SOLO el config de la entidad activa. Separado de set() porque el
   *  config cambia de referencia en cada render del panel (topContent y
   *  compañía son JSX nuevo cada vez) y no queremos que eso cuente como
   *  "cambió quién está activo" — ver los dos efectos de
   *  useRegisterAssistantActive. */
  updateConfig(entityType: string, entityId: string | null, config: AssistantModuleConfig): void {
    const cur = this.entry;
    if (!cur || cur.entityType !== entityType || cur.entityId !== entityId) return;
    this.entry = { ...cur, config };
    this.notify();
  }

  /**
   * Soltar la entidad al salir de su detalle (2026-08-30). El dock vivía
   * reteniendo la última entidad activa al navegar a una página que no
   * registra ninguna (las listas), y eso significaba que desde la lista de
   * proyectos el chat seguía apuntando al proyecto que habías abierto antes.
   * Mientras el catálogo sin entidad era casi todo lectura eso era confuso;
   * con la capa 1 (docs/PLAN_evobrain_tres_capas.md), donde una entidad
   * resuelta habilita las tools que la exigen, pasa a ser caro: "cambiá el
   * verde por azul" podía aplicarse a un proyecto que ya no estás mirando.
   *
   * Suelta la ENTIDAD, no el módulo: el dock sigue vivo y el asistente sigue
   * siendo el de ese módulo, solo que sin entidad implícita. Nombrar el
   * proyecto en el mensaje vuelve a resolverlo, explícitamente, vía find_*.
   *
   * Solo suelta si el par sigue siendo el activo: al ir del detalle A al
   * detalle B, si React montó B antes de desmontar A, el cleanup de A no
   * coincide y no pisa a B.
   */
  releaseEntity(entityType: string, entityId: string | null): void {
    const cur = this.entry;
    if (!cur || cur.entityType !== entityType || cur.entityId !== entityId) return;
    if (cur.entityId == null) return;
    this.entry = {
      entityType: cur.entityType,
      entityId: null,
      // El config se arma nombrando lo que SOBREVIVE, no borrando lo que se va:
      // así una propiedad nueva en AssistantModuleConfig no se cuela sola en un
      // estado sin entidad. Queda afuera todo lo que apuntaba a la entidad — el
      // link, el contenido fijado arriba (chips de leads, perfil de contactos),
      // las herramientas del módulo, la pestaña abierta y el draft de compose.
      config: {
        title: NEUTRAL_TITLE,
        placeholder: cur.config.placeholder,
        emptyHint: cur.config.emptyHint,
        entityLink: null,
        ...(cur.config.chipPlaceholder != null ? { chipPlaceholder: cur.config.chipPlaceholder } : {}),
        // Se conserva a propósito: es el render seguro de links del módulo de
        // correo (SafeLink, bloqueo de phishing). Soltar la entidad no puede
        // hacer que un hilo ya en pantalla pase a renderizar links crudos.
        ...(cur.config.renderAssistantContent != null
          ? { renderAssistantContent: cur.config.renderAssistantContent }
          : {}),
      },
    };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

/** Título del dock cuando se soltó la entidad. Nombre propio — igual en los dos
 *  idiomas, así que no lleva key de i18n. */
const NEUTRAL_TITLE = 'Evo';

interface Ctx {
  activeStore: ActiveStore;
  getApi: (entityType: string, entityId: string | null, section: string | undefined) => EntityAssistantApi;
  ensureLoaded: (entityType: string, entityId: string | null) => void;
}

const AssistantCtx = createContext<Ctx | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AssistantProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [entities, setEntities] = useState<Record<string, EntityState>>({});
  const activeStoreRef = useRef<ActiveStore>();
  activeStoreRef.current ??= new ActiveStore();
  const activeStore = activeStoreRef.current;

  // Control mutable por-entidad (no dispara render por sí solo)
  const loadedKeysRef = useRef<Set<string>>(new Set());
  const abortRef = useRef<Record<string, AbortController>>({});
  const isStreamingRef = useRef<Record<string, boolean>>({});

  // ── TTS — global, un solo canal de audio real ───────────────────────────────
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(TTS_MUTED_KEY) === 'true'; } catch { return false; }
  });
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsBroken, setTtsBroken] = useState(() => {
    try { return localStorage.getItem(TTS_BROKEN_KEY) === 'true'; } catch { return false; }
  });
  const ttsBrokenRef = useRef(ttsBroken);
  ttsBrokenRef.current = ttsBroken;
  const [ttsErrorMessage, setTtsErrorMessage] = useState<string | null>(() => {
    try { return localStorage.getItem(TTS_BROKEN_MSG_KEY); } catch { return null; }
  });

  const stopSpeaking = useCallback(() => {
    defaultTtsProvider.stop();
    setIsSpeaking(false);
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      try { localStorage.setItem(TTS_MUTED_KEY, String(next)); } catch { /* storage unavailable */ }
      if (next) { defaultTtsProvider.stop(); setIsSpeaking(false); }
      return next;
    });
  }, []);

  const reactivateTts = useCallback(() => {
    setTtsBroken(false);
    setTtsErrorMessage(null);
    try {
      localStorage.removeItem(TTS_BROKEN_KEY);
      localStorage.removeItem(TTS_BROKEN_MSG_KEY);
    } catch { /* storage unavailable */ }
  }, []);

  // Un fallo de PERMISO (cuenta suspendida, sesión vencida) no es "la voz está
  // rota": es una condición del entorno que se arregla sola cuando cambia, y
  // persistirla dejaba el cartel pegado para siempre. Pasó de verdad: el
  // onboarding devolvía 403 accountSuspended en /speak, el breaker se guardaba
  // en localStorage, y el cartel seguía ahí después de arreglar la causa —
  // incluso tras recargar, porque se releía del storage al montar.
  const isPermissionError = (msg: string) =>
    /accountSuspended|\b40[13]\b|no autoriz|unauthoriz|forbidden/i.test(msg)
    // El bloqueo de autoplay de Chrome llega como texto de error, pero NO es
    // una falla de la voz: es que todavía no hubo un gesto del usuario. El
    // proveedor ya reintenta solo al primer clic (lib/audioUnlock.ts), así que
    // persistir esto dejaría el cartel puesto por una condición que se resuelve
    // sola en el próximo clic.
    || /not allowed by the user agent|denied permission|NotAllowedError|autoplay/i.test(msg);

  const tripTtsBreaker = useCallback((err: Error) => {
    setIsSpeaking(false);
    setTtsBroken(true);
    setTtsErrorMessage(err.message);
    // Solo se persiste un fallo REAL de TTS (clave mal configurada, proveedor
    // caído). Los de permiso viven lo que dure la pestaña: al recargar
    // arrancan limpios y, si la causa sigue, se vuelven a marcar solos en el
    // primer intento.
    if (isPermissionError(err.message)) return;
    try {
      localStorage.setItem(TTS_BROKEN_KEY, 'true');
      localStorage.setItem(TTS_BROKEN_MSG_KEY, err.message);
    } catch { /* storage unavailable */ }
  }, []);

  const speakResponse = useCallback((text: string) => {
    if (mutedRef.current) return;
    const spoken = extractSpeakableText(text, {
      linkNote: t('assistant.tts.linkNote'),
      tableNote: t('assistant.tts.tableNote'),
    });
    if (!spoken.trim()) return;
    // Roto no corta el intento — antes `if (ttsBroken) return` significaba que,
    // una vez trabado, jamás se volvía a llamar a speak() y por lo tanto jamás
    // podía enterarse de que ya andaba de nuevo (ej. clave nueva cargada). Se
    // sigue intentando en cada respuesta; recién onStart real confirma que
    // volvió a andar y limpia el breaker solo.
    defaultTtsProvider.speak(spoken, {
      onStart: () => {
        setIsSpeaking(true);
        if (ttsBrokenRef.current) reactivateTts();
      },
      onEnd: () => setIsSpeaking(false),
      onError: tripTtsBreaker,
    });
  }, [t, tripTtsBreaker, reactivateTts]);

  // ── Helpers de estado por-entidad ────────────────────────────────────────────

  const patchEntity = useCallback((key: string, patch: Partial<EntityState> | ((prev: EntityState) => Partial<EntityState>)) => {
    setEntities(prev => {
      const current = prev[key] ?? EMPTY_ENTITY_STATE;
      const delta = typeof patch === 'function' ? patch(current) : patch;
      return { ...prev, [key]: { ...current, ...delta } };
    });
  }, []);

  // full=true trae la conversación entera; sin él, solo lo de hoy — el panel
  // arranca limpio cada día y lo anterior vive en el Historial (2026-08-31).
  const loadConversation = useCallback(async (
    entityType: string, entityId: string | null, key: string, full = false,
  ) => {
    const params = new URLSearchParams({ entityType });
    if (entityId) params.set('entityId', entityId);
    if (full) params.set('full', '1');
    try {
      const r = await fetch(`/api/assistant/conversation?${params}`);
      if (!r.ok) return;
      type RawMessage = Omit<AssistantMessage, 'proposal'> & {
        proposal?: { taskId: string; turnGroupId: string; verb: ProposalVerb; summary: ProposalSummary } | null;
      };
      const data = await r.json() as {
        conversationId: number | null; messages: RawMessage[]; hasOlderMessages?: boolean;
      };
      const mapped: AssistantMessage[] = (data.messages ?? []).map(m => ({
        ...m,
        proposal: m.proposal
          ? { ...m.proposal, status: (m.toolStatus as 'pending' | 'approved' | 'rejected' | null) ?? 'pending' }
          : null,
      }));
      patchEntity(key, {
        messages: mapped,
        conversationId: data.conversationId,
        hasOlderMessages: full ? false : (data.hasOlderMessages ?? false),
        showingFullHistory: full,
        loaded: true,
      });
    } catch {
      // non-fatal — conversation may not exist yet. `loaded` igual: quien
      // espera para decidir (el saludo de inducción) no puede quedarse
      // esperando para siempre porque una petición falló.
      patchEntity(key, { loaded: true });
    }
  }, [patchEntity]);

  /** "Ver lo anterior" desde el propio panel: carga la conversación completa
   *  sin obligar a salir a Historial por algo que se dijo ayer. */
  const loadFullConversation = useCallback((entityType: string, entityId: string | null) => {
    void loadConversation(entityType, entityId, entityKeyOf(entityType, entityId), true);
  }, [loadConversation]);

  const loadPills = useCallback(async (entityType: string, entityId: string | null, key: string) => {
    const params = new URLSearchParams({ module: entityType, hasEntity: entityId != null ? '1' : '0' });
    try {
      const r = await fetch(`/api/assistant/pills?${params}`);
      if (!r.ok) return;
      const data = await r.json() as { pills: AssistantPill[] };
      patchEntity(key, { pills: data.pills ?? [] });
    } catch {
      // non-fatal
    }
  }, [patchEntity]);

  const ensureLoaded = useCallback((entityType: string, entityId: string | null) => {
    const key = entityKeyOf(entityType, entityId);
    if (loadedKeysRef.current.has(key)) return;
    loadedKeysRef.current.add(key);
    void loadConversation(entityType, entityId, key);
    void loadPills(entityType, entityId, key);
  }, [loadConversation, loadPills]);

  const invalidateRelated = useCallback((entityType: string) => {
    const keys = INVALIDATION_KEYS[entityType];
    if (!keys) return;
    for (const k of keys) void qc.invalidateQueries({ queryKey: k });
  }, [qc]);
  const invalidateTasks = useCallback(() => { void qc.invalidateQueries({ queryKey: ['tasks'] }); }, [qc]);

  // ── sendMessage (SSE) — misma lógica que antes, ahora parametrizada por key ──

  const sendMessage = useCallback(async (entityType: string, entityId: string | null, section: string | undefined, userMessage: string, composeContext?: ComposeContext) => {
    const key = entityKeyOf(entityType, entityId);
    const trimmed = userMessage.trim();
    if (!trimmed || isStreamingRef.current[key]) return;

    abortRef.current[key]?.abort();
    const controller = new AbortController();
    abortRef.current[key] = controller;
    isStreamingRef.current[key] = true;

    patchEntity(key, prev => ({
      messages: [...prev.messages, {
        id: -Date.now(), role: 'user', content: trimmed, toolName: null, toolStatus: null,
        isUndone: 0, humanSummary: null, createdAt: new Date().toISOString(), undoable: false,
      }],
      error: null, voiceNote: null, streaming: { phase: 'thinking' },
    }));

    let finalVoiceText: string | null | undefined;

    function handleEvent(event: SseEvent) {
      switch (event.type) {
        case 'thinking':
          patchEntity(key, { streaming: { phase: 'thinking' } });
          break;
        case 'tool_executing':
          patchEntity(key, { streaming: { phase: 'tool_executing', toolLabel: event.label, toolName: event.toolName } });
          break;
        case 'proposal':
          patchEntity(key, prev => ({
            messages: [...prev.messages, {
              id: event.messageId, role: 'tool_proposal', content: event.summary.sentence,
              toolName: event.toolName, toolStatus: 'pending', isUndone: 0,
              humanSummary: event.summary.sentence, createdAt: new Date().toISOString(), undoable: false,
              proposal: { taskId: event.taskId, turnGroupId: event.turnGroupId, verb: event.verb, summary: event.summary, status: 'pending' },
            }],
            streaming: { phase: 'thinking' },
          }));
          break;
        case 'tool_result':
          patchEntity(key, prev => ({
            messages: [...prev.messages, {
              id: event.messageId, role: 'tool_result', content: event.humanSummary,
              toolName: event.toolName, toolStatus: 'done', isUndone: 0,
              humanSummary: event.humanSummary, createdAt: new Date().toISOString(), undoable: event.undoable,
            }],
            streaming: { phase: 'thinking' },
          }));
          // open_module/open_entity (navegación por voz) — el path real lo
          // arma el server (ver global.ts), esto solo lo lee y cambia de
          // pantalla. La confirmación hablada de "adónde te llevó" es texto
          // normal del modelo, no algo especial acá.
          {
            const navigatePath = extractNavigatePath(event.result);
            if (navigatePath) navigate(navigatePath);
          }
          break;
        case 'text':
          finalVoiceText = event.voice;
          patchEntity(key, prev => ({
            streaming: { phase: 'text', textAccum: (prev.streaming.textAccum ?? '') + event.content },
          }));
          break;
        case 'done':
          isStreamingRef.current[key] = false;
          void loadConversation(entityType, entityId, key);
          invalidateRelated(entityType);
          patchEntity(key, {
            conversationId: event.conversationId,
            streaming: { phase: 'idle' },
            voiceNote: finalVoiceText ? null : finalVoiceText === '' ? t('assistant.tts.silentNote') : t('assistant.tts.missingNote'),
          });
          if (finalVoiceText) speakResponse(finalVoiceText);
          break;
        case 'error':
          isStreamingRef.current[key] = false;
          patchEntity(key, { error: event.error, streaming: { phase: 'idle' } });
          break;
      }
    }

    try {
      const response = await fetch('/api/assistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ entityType, entityId, message: trimmed, ...(section ? { section } : {}), ...(composeContext ? { composeContext } : {}) }),
        signal: controller.signal,
      });
      if (!response.ok) {
        const body = await response.json() as { error?: string };
        throw new Error(body.error ?? `HTTP ${response.status}`);
      }
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          const raw = frame.startsWith('data: ') ? frame.slice(6) : frame;
          if (!raw.trim()) continue;
          try { handleEvent(JSON.parse(raw) as SseEvent); } catch { /* skip malformed frame */ }
        }
      }
    } catch (err: unknown) {
      if ((err as Error).name === 'AbortError') return;
      isStreamingRef.current[key] = false;
      patchEntity(key, { error: (err as Error).message ?? 'Error desconocido', streaming: { phase: 'idle' } });
    }
  }, [patchEntity, loadConversation, invalidateRelated, speakResponse, t, navigate]);

  // ── Undo / approve / reject — misma lógica que antes, parametrizada por key ──

  const undoMessage = useCallback(async (entityType: string, key: string, messageId: number) => {
    patchEntity(key, { isUndoing: true, error: null });
    try {
      const r = await fetch('/api/assistant/undo', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ messageId }),
      });
      if (!r.ok) { const body = await r.json() as { error?: string }; throw new Error(body.error ?? `HTTP ${r.status}`); }
      patchEntity(key, prev => ({ messages: prev.messages.map(m => m.id === messageId ? { ...m, isUndone: 1 } : m) }));
      invalidateRelated(entityType);
    } catch (err: unknown) {
      patchEntity(key, { error: (err as Error).message });
    } finally {
      patchEntity(key, { isUndoing: false });
    }
  }, [patchEntity, invalidateRelated]);

  const approveProposal = useCallback(async (entityType: string, entityId: string | null, key: string, taskId: string, remember?: boolean) => {
    patchEntity(key, { approvingTaskId: taskId, error: null });
    try {
      const r = await fetch(`/api/tasks/${taskId}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ rememberApproval: Boolean(remember) }),
      });
      if (!r.ok) { const body = await r.json() as { error?: string }; throw new Error(body.error ?? `HTTP ${r.status}`); }
      await loadConversation(entityType, entityId, key);
      invalidateRelated(entityType);
      invalidateTasks();
    } catch (err: unknown) {
      patchEntity(key, { error: (err as Error).message });
    } finally {
      patchEntity(key, { approvingTaskId: null });
    }
  }, [patchEntity, loadConversation, invalidateRelated, invalidateTasks]);

  const rejectProposal = useCallback(async (entityType: string, entityId: string | null, key: string, taskId: string) => {
    patchEntity(key, { rejectingTaskId: taskId, error: null });
    try {
      const r = await fetch(`/api/tasks/${taskId}/reject`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!r.ok) { const body = await r.json() as { error?: string }; throw new Error(body.error ?? `HTTP ${r.status}`); }
      await loadConversation(entityType, entityId, key);
      invalidateTasks();
    } catch (err: unknown) {
      patchEntity(key, { error: (err as Error).message });
    } finally {
      patchEntity(key, { rejectingTaskId: null });
    }
  }, [patchEntity, loadConversation, invalidateTasks]);

  const approveAllProposals = useCallback(async (entityType: string, entityId: string | null, key: string, taskIds: string[]) => {
    patchEntity(key, { bulkApproving: true, error: null });
    try {
      for (const id of taskIds) {
        const r = await fetch(`/api/tasks/${id}/approve`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!r.ok) { const body = await r.json() as { error?: string }; throw new Error(body.error ?? `HTTP ${r.status}`); }
      }
      await loadConversation(entityType, entityId, key);
      invalidateRelated(entityType);
      invalidateTasks();
    } catch (err: unknown) {
      patchEntity(key, { error: (err as Error).message });
    } finally {
      patchEntity(key, { bulkApproving: false });
    }
  }, [patchEntity, loadConversation, invalidateRelated, invalidateTasks]);

  const rejectAllProposals = useCallback(async (entityType: string, entityId: string | null, key: string, taskIds: string[]) => {
    patchEntity(key, { bulkRejecting: true, error: null });
    try {
      for (const id of taskIds) {
        const r = await fetch(`/api/tasks/${id}/reject`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!r.ok) { const body = await r.json() as { error?: string }; throw new Error(body.error ?? `HTTP ${r.status}`); }
      }
      await loadConversation(entityType, entityId, key);
      invalidateTasks();
    } catch (err: unknown) {
      patchEntity(key, { error: (err as Error).message });
    } finally {
      patchEntity(key, { bulkRejecting: false });
    }
  }, [patchEntity, loadConversation, invalidateTasks]);

  // ── getApi — arma el objeto que cada página consume (misma forma que el viejo useAssistant()) ──

  const getApi = useCallback((entityType: string, entityId: string | null, section: string | undefined): EntityAssistantApi => {
    const key = entityKeyOf(entityType, entityId);
    const state = entities[key] ?? EMPTY_ENTITY_STATE;
    return {
      ...state,
      muted, toggleMuted, isSpeaking, stopSpeaking, ttsBroken, ttsErrorMessage, reactivateTts,
      sendMessage: (msg: string, composeContext?: ComposeContext) => sendMessage(entityType, entityId, section, msg, composeContext),
      undoMessage: (messageId: number) => undoMessage(entityType, key, messageId),
      approveProposal: (taskId: string, remember?: boolean) => approveProposal(entityType, entityId, key, taskId, remember),
      rejectProposal: (taskId: string) => rejectProposal(entityType, entityId, key, taskId),
      approveAllProposals: (taskIds: string[]) => approveAllProposals(entityType, entityId, key, taskIds),
      rejectAllProposals: (taskIds: string[]) => rejectAllProposals(entityType, entityId, key, taskIds),
      loadFullHistory: () => loadFullConversation(entityType, entityId),
    };
  }, [entities, muted, toggleMuted, isSpeaking, stopSpeaking, ttsBroken, ttsErrorMessage, reactivateTts,
    sendMessage, undoMessage, approveProposal, rejectProposal, approveAllProposals, rejectAllProposals,
    loadFullConversation]);

  // ── Canal en vivo de la conversación abierta ────────────────────────────────
  // Un trabajo largo (una tarea que corre en la Mac) escribe en la conversación
  // por fuera de un turno. Hasta el 2026-09-03 eso era invisible: el panel
  // cargaba la conversación al montarse y nunca más — ni polling, ni stream, ni
  // refetch al volver. El aviso quedaba escrito y no se veía hasta navegar y
  // volver, que es cómo un cambio de código terminado pasó desapercibido.
  //
  // Una sola conexión, la de la entidad ACTIVA: las demás no se están mirando,
  // y cuando se vuelva a una se recarga al montarse igual.
  //
  // El evento no trae el mensaje, solo avisa que hay novedad — el shape del
  // mensaje vive en un solo lugar (loadConversation) y no en dos.

  const live = useSyncExternalStore(activeStore.subscribe, activeStore.getSnapshot);
  const liveType = live?.entityType ?? null;
  const liveId = live?.entityId ?? null;

  useEffect(() => {
    if (liveType == null) return;
    const key = entityKeyOf(liveType, liveId);

    const recargar = (): void => {
      // Durante un turno NO se recarga: el propio turno recarga al terminar
      // (evento 'done'), y hacerlo a mitad pisaría el texto que se está
      // acumulando en pantalla con una versión sin él.
      if (isStreamingRef.current[key]) return;
      void loadConversation(liveType, liveId, key);
    };

    const params = new URLSearchParams({ entityType: liveType });
    if (liveId) params.set('entityId', liveId);
    const source = new EventSource(`/api/assistant/stream?${params}`);
    source.onmessage = recargar;
    // Sin onerror explícito: EventSource reconecta solo. Loguear cada corte
    // llenaría la consola cada vez que la laptop se duerme.

    // Red de seguridad, no optimización: cubre el rato que estuvo dormida (los
    // eventos de ese lapso no se reenvían) y el día que el server pase a modo
    // cluster, donde el canal en memoria dejaría de alcanzar en silencio.
    const alVolver = (): void => { if (document.visibilityState === 'visible') recargar(); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);

    return () => {
      source.close();
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [liveType, liveId, loadConversation]);

  const value = useMemo<Ctx>(
    () => ({ activeStore, getApi, ensureLoaded }),
    [activeStore, getApi, ensureLoaded],
  );

  return <AssistantCtx.Provider value={value}>{children}</AssistantCtx.Provider>;
}

function useAssistantCtx(): Ctx {
  const ctx = useContext(AssistantCtx);
  if (!ctx) throw new Error('useAssistantEntity/useActiveAssistant requiere <AssistantProvider>.');
  return ctx;
}

// ── Hook de lectura — llamado por cada página ─────────────────────────────────
// Devuelve la misma API que antes devolvía useAssistant() directo (mensajes,
// sendMessage, streaming, etc) — las páginas que necesitan esto para construir
// SU contenido (chips de leads, quick actions de contacts, auto-disparo de
// home/onboarding) lo siguen teniendo. A propósito NO registra nada como
// activo todavía — separado de useRegisterAssistantActive de abajo, porque el
// config que se registra (topContent = los chips) necesita `messages` de ACÁ
// primero (si fuera un solo hook, se pisarían: no se puede construir topContent
// antes de tener los mensajes que ese mismo hook devolvería).
export function useAssistantModule(
  entityType: string,
  entityId: string | null,
  section: string | undefined,
): EntityAssistantApi {
  const { getApi, ensureLoaded } = useAssistantCtx();

  useLayoutEffect(() => {
    ensureLoaded(entityType, entityId);
  }, [entityType, entityId, ensureLoaded]);

  return getApi(entityType, entityId, section);
}

// ── Hook de registro — llamado por cada página, DESPUÉS de useAssistantModule ─
// Marca "esta es la entidad activa" — síncrono (useLayoutEffect), antes del
// paint, para que el dock nunca muestre un frame con la entidad vieja al
// navegar. Escribe en activeStore (ver clase arriba), NUNCA en React state
// compartido con quien escribe — por diseño no puede haber loop, pero el
// array de dependencias abajo además evita reescribir en cada render sin
// necesidad (entityType/entityId + los campos primitivos de config; los
// campos no primitivos — topContent, renderAssistantContent — sí entran en
// cada comparación porque son nuevas referencias cada vez, así que la
// escritura ocurre en cada render donde el panel mismo re-renderiza — sigue
// siendo seguro, solo un poco más de trabajo que el mínimo posible).
export function useRegisterAssistantActive(
  entityType: string,
  entityId: string | null,
  config: AssistantModuleConfig,
): void {
  const { activeStore } = useAssistantCtx();

  // Efecto 1 — QUIÉN está activo. Depende solo del par (entityType, entityId),
  // así que corre al montar y al cambiar de entidad, no en cada render. Su
  // cleanup es el que suelta la entidad al desmontar el panel (salir del
  // detalle) — ver releaseEntity. Con el config en el mismo efecto, el cleanup
  // habría corrido en cada render del panel (topContent es JSX nuevo cada vez)
  // y la entidad se habría soltado y vuelto a poner sin motivo.
  //
  // configRef: el set inicial necesita el config del render actual sin que el
  // config entre como dependencia. Lo lee, no lo observa.
  const configRef = useRef(config);
  configRef.current = config;

  useLayoutEffect(() => {
    activeStore.set({ entityType, entityId, config: configRef.current });
    return () => { activeStore.releaseEntity(entityType, entityId); };
  }, [activeStore, entityType, entityId]);

  // Efecto 2 — CÓMO se muestra. Actualiza el config del par activo; si otro
  // panel ya tomó el lugar, updateConfig no hace nada (compara el par).
  // `config` entero como dependencia dispararía en cada render (topContent y
  // compañía son JSX nuevo cada vez); se listan sus campos, que es lo que de
  // verdad cambia.
  useLayoutEffect(() => {
    activeStore.updateConfig(entityType, entityId, config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStore, entityType, entityId,
    config.title, config.placeholder, config.inputDisabled, config.emptyHint, config.chipPlaceholder,
    config.entityLink?.label, config.entityLink?.href,
    config.topContent, config.toolsContent, config.activeTab, config.onActiveTabChange,
    config.renderAssistantContent, config.composeContext,
  ]);
}

// ── Hook de lectura — usado por AssistantDock y VoiceConversationOverlay ────
// Dos lectores hoy (antes uno) — ningún panel se suscribe a esto, solo
// escriben, así que agregar un segundo lector no reabre el riesgo de loop
// descrito arriba (ese era sobre ESCRITURA). useSyncExternalStore soporta
// múltiples suscriptores por diseño — un store fuera de React state, con
// snapshot estable entre notificaciones.
export function useActiveAssistant(): { entityType: string; entityId: string | null; config: AssistantModuleConfig; api: EntityAssistantApi } | null {
  const { activeStore, getApi, ensureLoaded } = useAssistantCtx();
  const active = useSyncExternalStore(activeStore.subscribe, activeStore.getSnapshot);

  // Al soltar la entidad (releaseEntity) el activo pasa a un par —
  // (entityType, null) — que puede no tener ningún panel montado que haya
  // pedido su conversación. Sin esto, el dock mostraría vacío un hilo que sí
  // existe en el servidor hasta que mandaras un mensaje. ensureLoaded es
  // idempotente; antes del early return por las reglas de hooks.
  const activeType = active?.entityType ?? null;
  const activeId = active?.entityId ?? null;
  useEffect(() => {
    if (activeType == null) return;
    ensureLoaded(activeType, activeId);
  }, [activeType, activeId, ensureLoaded]);

  if (!active) return null;
  const api = getApi(active.entityType, active.entityId, undefined);
  return { entityType: active.entityType, entityId: active.entityId, config: active.config, api };
}
